import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTour } from "app-context/tourContext/TourContext";
import Button from "component/atom/forms/button/Button";
import styles from "./TourModal.module.scss";
import SvgWithBranding from "../branding/withBrandingColor.hoc";
import quickTourArrow from "../../../assets/icons/vector/quick-tour-arrow.svg";

const TourModal = () => {
  const {
    activeTour,
    currentPageIndex,
    currentStepIndex,
    showModal,
    nextStep,
    stopTour,
  } = useTour();
  const navigate = useNavigate();
  const location = useLocation();

  const currentPage = activeTour?.pages[currentPageIndex];
  const currentStepConfig = currentPage?.steps[currentStepIndex];
  const position = currentStepConfig?.position || "center";
  const modalOffset = currentStepConfig?.modalOffset || { x: 0, y: 0 };

  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    if (!currentStepConfig?.targetElement) return;

    const updatePosition = () => {
      const element = document.querySelector(currentStepConfig.targetElement);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect({
          top: rect.top + window.scrollY,
          bottom: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          right: rect.right + window.scrollX,
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updatePosition();

    const observer = new MutationObserver(updatePosition);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition);
    };
  }, [currentStepConfig?.targetElement]);

  useEffect(() => {
    if (currentStepConfig?.action) {
      currentStepConfig.action();
    }
  }, [currentStepIndex, currentPageIndex, currentStepConfig]);

  useEffect(() => {
    if (location.pathname === "/bots") {
      setTimeout(() => {
        const event = new CustomEvent("openBotManagementModal");
        window.dispatchEvent(event);
      }, 500);
    }
  }, [location.pathname]);

  if (!showModal || !activeTour) return null;

  const isFirstPage = currentPageIndex === 0;
  const isFirstStep = currentStepIndex === 0;
  const lastPageIndex = activeTour.pages.length - 1;
  const lastStepIndex = activeTour.pages[lastPageIndex].steps.length - 1;

  const isFinalStep =
    currentPageIndex === lastPageIndex && currentStepIndex === lastStepIndex;
  const isWelcomeModal =
    isFirstPage && isFirstStep && location.pathname === "/dashboard";

  const getModalStyle = () => {
    const baseStyle = {
      position: "fixed",
      zIndex: 1001,
      maxWidth: "800px",
      width: "90%",
    };

    if (!targetRect || position === "center") {
      return {
        ...baseStyle,
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    const offsetX = modalOffset?.x ?? 0;
    const offsetY = modalOffset?.y ?? 0;

    switch (position) {
      case "top":
        return {
          ...baseStyle,
          top: `${targetRect.top - 10 + (modalOffset.y || 0)}px`,
          left: `${
            targetRect.left + targetRect.width / 2 + (modalOffset.x ?? 0)
          }px`,
          transform: "translateX(-50%)",
        };
      case "bottom":
        return {
          ...baseStyle,
          top: `${targetRect.bottom + 10 + offsetY}px`,
          left: `${targetRect.left + targetRect.width / 2}px`,
          transform: "translateX(-50%)",
        };
      case "left":
        return {
          ...baseStyle,
          top: `${targetRect.top + targetRect.height / 2}px`,
          left: `${targetRect.left - 10 + offsetX}px`,
          transform: "translateY(-50%)",
        };
      case "right":
        return {
          ...baseStyle,
          top: `${targetRect.top + targetRect.height / 2}px`,
          left: `${targetRect.right + 10 + offsetX}px`,
          transform: "translateY(-50%)",
        };
      default:
        return baseStyle;
    }
  };

  const handleNext = () => {
    if (isFinalStep) {
      if (location.pathname === "/knowledge-base") {
        navigate("/bots");
      } else {
        stopTour();
      }
    } else {
      nextStep();
    }
  };

  if (isWelcomeModal) {
    return (
      <div className={styles["modal-overlay"]}>
        <div
          className={`${styles["modal-content"]} ${styles["welcome-modal"]}`}
        >
          <div className={styles["welcome-modal-container"]}>
            <img
              src="/images/default-bot.svg"
              alt="Welcome"
              className={styles["welcome-modal-bot-icon"]}
            />
            <p className={styles["welcome-title"]}>
              {currentStepConfig.content}
            </p>
            <Button
              onClick={handleNext}
              label="Start a Quick Tour"
              variant="primary"
              width="311px"
            />
            <span onClick={stopTour} className={styles["skip-tour"]}>
              Skip the tour
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["modal-overlay"]}>
      <div
        className={`${styles["modal-content"]} ${styles["tour-step-modal"]}`}
        style={getModalStyle()}
      >
        <img
          src={quickTourArrow}
          className={
            currentStepConfig?.arrowRight
              ? styles["arrow-right"]
              : styles["modal-arrow"]
          }
          alt="Tour indicator"
        />
        <div className={styles["title-container"]}>
          <SvgWithBranding
            type="exit_modal"
            onClick={stopTour}
            className={styles["exit-img"]}
          />
        </div>
        <div className={styles["modal-body"]}>
          <img src={currentStepConfig?.image} alt="" />
          <div className={styles["modal-description-container"]}>
            <p className={styles["modal-title"]}>{currentStepConfig?.title}</p>
            <p className={styles["modal-description"]}>
              {currentStepConfig?.content}
            </p>
          </div>
        </div>
        <div className={styles["modal-footer"]}>
          <div className={styles["step-indicators"]}>
            {currentPage.steps.map((_, index) => (
              <div
                key={index}
                className={`${styles["step-dot"]} ${
                  index === currentStepIndex ? styles["active"] : ""
                }`}
              />
            ))}
          </div>
          <Button
            onClick={handleNext}
            label={isFinalStep ? "Finish" : "Next"}
            variant="primary"
            width="311px"
          />
        </div>
      </div>
    </div>
  );
};

export default TourModal;
